// MultiOrgReportsTab.js — Сравнительные отчёты по нескольким организациям
import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { useTheme, fmt, fmtDay, Card, PageHeader, TabSwitch, loadOrg } from './shared';
import { S } from './strings';
import { TrendingUp, Building2, ArrowUpRight, ArrowDownRight } from 'lucide-react';

export default function MultiOrgReportsTab({ userOrgs, meta, isCompact }) {
  const t = useTheme();
  const [period, setPeriod] = useState('month'); // 'month' | 'quarter' | 'year'

  // Загружаем данные всех организаций
  const allOrgData = useMemo(() => {
    return userOrgs.map(o => {
      const data = loadOrg(o.id);
      return { ...o, data };
    }).filter(o => o.data);
  }, [userOrgs]);

  // Определяем границы периода
  const now = new Date();
  const periodStart = useMemo(() => {
    const d = new Date(now);
    if (period === 'month') d.setDate(1);
    else if (period === 'quarter') { d.setMonth(Math.floor(d.getMonth() / 3) * 3, 1); }
    else d.setMonth(0, 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [period]);

  // Считаем метрики каждой орг за период
  const orgMetrics = useMemo(() => {
    return allOrgData.map(o => {
      const txs = (o.data.transactions || []).filter(tx => new Date(tx.date) >= periodStart);
      const income = txs.filter(tx => tx.type === 'income').reduce((s, tx) => s + (tx.amount || 0), 0);
      const expense = txs.filter(tx => tx.type === 'expense').reduce((s, tx) => s + (tx.amount || 0), 0);
      const profit = income - expense;
      const totalBalance = Object.values(o.data.accountBalances || {}).reduce((s, v) => s + (v || 0), 0);
      const txCount = txs.length;

      // Топ категорий расходов
      const expByCat = {};
      txs.filter(tx => tx.type === 'expense').forEach(tx => {
        const cat = tx.expenseCategoryName || S().common.noCategoryShort;
        expByCat[cat] = (expByCat[cat] || 0) + (tx.amount || 0);
      });
      const topExpenses = Object.entries(expByCat).sort((a, b) => b[1] - a[1]).slice(0, 5);

      return {
        id: o.id, name: o.name, color: o.color || t.accent,
        income, expense, profit, totalBalance, txCount, topExpenses,
      };
    });
  }, [allOrgData, periodStart, t.accent]);

  // Суммарные значения
  const totals = useMemo(() => ({
    income: orgMetrics.reduce((s, m) => s + m.income, 0),
    expense: orgMetrics.reduce((s, m) => s + m.expense, 0),
    profit: orgMetrics.reduce((s, m) => s + m.profit, 0),
    balance: orgMetrics.reduce((s, m) => s + m.totalBalance, 0),
  }), [orgMetrics]);

  // Данные для графиков
  const chartData = orgMetrics.map(m => ({
    name: m.name.length > 12 ? m.name.slice(0, 12) + '..' : m.name,
    income: m.income, expense: m.expense, profit: m.profit,
  }));

  const pieData = orgMetrics.filter(m => m.income > 0).map(m => ({
    name: m.name, value: m.income, color: m.color,
  }));

  const periodLabel = period === 'month' ? S().multiOrg.currentMonth : period === 'quarter' ? S().multiOrg.currentQuarter : S().multiOrg.currentYear;

  if (allOrgData.length < 2) {
    return (
      <div>
        <PageHeader title={S().multiOrg.title} sub={S().multiOrg.needMinOrgs} />
        <Card><div style={{ textAlign: 'center', padding: 40, color: t.textMuted }}>{S().multiOrg.addOneMoreOrg}</div></Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={S().multiOrg.title} sub={S().multiOrg.periodLabel(periodLabel, allOrgData.length)} />

      {/* Фильтр периода */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {[['month', S().multiOrg.periodMonth], ['quarter', S().multiOrg.periodQuarter], ['year', S().multiOrg.periodYear]].map(([val, label]) => (
          <button key={val} onClick={() => setPeriod(val)} style={{
            padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            background: period === val ? t.accent : t.bgInput,
            color: period === val ? '#fff' : t.textMuted,
            border: `1px solid ${period === val ? t.accent : t.border}`,
          }}>{label}</button>
        ))}
      </div>

      {/* Сводные карточки */}
      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{S().multiOrg.totalIncome}</div>
          <div className="mono" style={{ fontSize: 18, fontWeight: 800, color: t.accentGreen, marginTop: 4 }}>{fmt(totals.income)}</div>
        </Card>
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{S().multiOrg.totalExpense}</div>
          <div className="mono" style={{ fontSize: 18, fontWeight: 800, color: t.accentRed, marginTop: 4 }}>{fmt(totals.expense)}</div>
        </Card>
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{S().multiOrg.netProfit}</div>
          <div className="mono" style={{ fontSize: 18, fontWeight: 800, color: totals.profit >= 0 ? t.accentGreen : t.accentRed, marginTop: 4 }}>{fmt(totals.profit)}</div>
        </Card>
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{S().multiOrg.onAccounts}</div>
          <div className="mono" style={{ fontSize: 18, fontWeight: 800, color: t.text, marginTop: 4 }}>{fmt(totals.balance)}</div>
        </Card>
      </div>

      {/* Сравнительная таблица */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>{S().multiOrg.comparison}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: t.textMuted, fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>{S().multiOrg.thOrg}</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: t.textMuted, fontWeight: 700, fontSize: 11 }}>{S().multiOrg.thIncome}</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: t.textMuted, fontWeight: 700, fontSize: 11 }}>{S().multiOrg.thExpense}</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: t.textMuted, fontWeight: 700, fontSize: 11 }}>{S().multiOrg.thProfit}</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: t.textMuted, fontWeight: 700, fontSize: 11 }}>{S().multiOrg.thBalance}</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: t.textMuted, fontWeight: 700, fontSize: 11 }}>{S().multiOrg.thOps}</th>
              </tr>
            </thead>
            <tbody>
              {orgMetrics.map(m => (
                <tr key={m.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: 6, background: m.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>{m.name.charAt(0)}</div>
                      <span style={{ fontWeight: 600 }}>{m.name}</span>
                    </div>
                  </td>
                  <td className="mono" style={{ textAlign: 'right', padding: '10px 12px', color: t.accentGreen, fontWeight: 600 }}>{fmt(m.income)}</td>
                  <td className="mono" style={{ textAlign: 'right', padding: '10px 12px', color: t.accentRed, fontWeight: 600 }}>{fmt(m.expense)}</td>
                  <td className="mono" style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 700, color: m.profit >= 0 ? t.accentGreen : t.accentRed }}>
                    {m.profit >= 0 ? <ArrowUpRight size={12} style={{ verticalAlign: 'middle' }} /> : <ArrowDownRight size={12} style={{ verticalAlign: 'middle' }} />}
                    {fmt(m.profit)}
                  </td>
                  <td className="mono" style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600 }}>{fmt(m.totalBalance)}</td>
                  <td style={{ textAlign: 'right', padding: '10px 12px', color: t.textMuted }}>{m.txCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Графики */}
      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '2fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Сравнительный бар */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>{S().multiOrg.incomeVsExpense}</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.border} />
              <XAxis dataKey="name" tick={{ fill: t.textMuted, fontSize: 11 }} />
              <YAxis tick={{ fill: t.textMuted, fontSize: 11 }} tickFormatter={v => v >= 1000000 ? (v/1000000).toFixed(1)+'M' : v >= 1000 ? (v/1000).toFixed(0)+'K' : v} />
              <Tooltip contentStyle={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 12 }} formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="income" name={S().multiOrg.incomeBarName} fill={t.accentGreen} radius={[4,4,0,0]} />
              <Bar dataKey="expense" name={S().multiOrg.expenseBarName} fill={t.accentRed} radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Пай-чарт доходов */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>{S().multiOrg.incomeShare}</div>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 10 }}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: t.textMuted, fontSize: 13 }}>{S().multiOrg.noIncomeForPeriod}</div>
          )}
        </Card>
      </div>

      {/* Топ расходов по каждой орг */}
      <Card>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>{S().multiOrg.topExpenses}</div>
        <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : `repeat(${Math.min(allOrgData.length, 3)}, 1fr)`, gap: 16 }}>
          {orgMetrics.map(m => (
            <div key={m.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, background: m.color }} />
                <span style={{ fontSize: 13, fontWeight: 700 }}>{m.name}</span>
              </div>
              {m.topExpenses.length > 0 ? m.topExpenses.map(([cat, amt], i) => (
                <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: i < m.topExpenses.length - 1 ? `1px solid ${t.border}` : 'none' }}>
                  <span style={{ fontSize: 12, color: t.textSec }}>{cat}</span>
                  <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: t.accentRed }}>{fmt(amt)}</span>
                </div>
              )) : (
                <div style={{ fontSize: 12, color: t.textMuted }}>{S().multiOrg.noExpenses}</div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
